import type { MethodGraph, MethodNode } from '../schemas/index.js';
import {
  CONTAINMENT_TERMS,
  NODE_TYPES,
  REFERENCE_TARGETS,
  TYPE_ALIASES,
  type ContainmentTerm,
  type NodeTypeIri,
  type ReferenceTerm,
} from '../vocabulary/index.js';
import type { Diagnostic, Rule, ValidateOptions } from './diagnostics.js';
import { indexGraph, isBlank, nodesOfType, type GraphIndex } from './graph-index.js';

export type RuleFinding = Omit<Diagnostic, 'ruleId' | 'severity'>;

export interface RuleContext {
  readonly graph: MethodGraph;
  readonly index: GraphIndex;
  readonly options: ValidateOptions;
}

export interface RuleImplementation extends Rule {
  run(context: RuleContext): readonly RuleFinding[];
}

/**
 * Union-wide field read. Rules are driven by the term table rather than by the
 * concrete node type, so they address fields by name; the node schemas have
 * already guaranteed the value types behind each name.
 */
function field(node: MethodNode, key: string): unknown {
  return (node as unknown as Record<string, unknown>)[key];
}

function stringField(node: MethodNode, key: string): string | undefined {
  const value = field(node, key);
  return typeof value === 'string' ? value : undefined;
}

function numberField(node: MethodNode, key: string): number | undefined {
  const value = field(node, key);
  return typeof value === 'number' ? value : undefined;
}

function booleanField(node: MethodNode, key: string): boolean | undefined {
  const value = field(node, key);
  return typeof value === 'boolean' ? value : undefined;
}

function idsField(node: MethodNode, key: string): readonly string[] {
  const value = field(node, key);
  if (typeof value === 'string') return [value];
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
  return [];
}

/**
 * Reference terms whose absence is diagnosed by a dedicated rule. Excluding them
 * from MTH-001 keeps one problem reported once, which matters because the UI
 * validation table is grouped by rule.
 */
const SPECIALISED_REFERENCE_TERMS: readonly ReferenceTerm[] = [
  'variantOf',
  'hasTemplateVersion',
  'currentTemplateVersion',
  'templateOverride',
];

/** Terms where pointing at yourself is always a mistake. */
const NON_REFLEXIVE_TERMS: readonly ReferenceTerm[] = [
  'precedes',
  'dependsOn',
  'loopsTo',
  'parentPractice',
  'variantOf',
  'hasWorkPractice',
  'hasActivity',
  'hasStage',
  'hasPhase',
];

function edgeMap(index: GraphIndex, term: ReferenceTerm): ReadonlyMap<string, readonly string[]> {
  const edges = new Map<string, string[]>();
  for (const reference of index.references) {
    if (reference.property !== term) continue;
    const bucket = edges.get(reference.from);
    if (bucket === undefined) edges.set(reference.from, [reference.to]);
    else bucket.push(reference.to);
  }
  return edges;
}

/**
 * Every simple cycle reachable in `edges`, each reported once. Canonicalised by
 * rotating the smallest id to the front so the same cycle found from two entry
 * points is not reported twice.
 */
function findCycles(edges: ReadonlyMap<string, readonly string[]>): readonly (readonly string[])[] {
  const state = new Map<string, 'open' | 'done'>();
  const found = new Map<string, readonly string[]>();

  const visit = (id: string, path: readonly string[]): void => {
    const current = state.get(id);
    if (current === 'done') return;
    if (current === 'open') {
      const start = path.indexOf(id);
      if (start < 0) return;
      const cycle = path.slice(start);
      let pivot = 0;
      for (let i = 1; i < cycle.length; i += 1) {
        const candidate = cycle[i];
        const best = cycle[pivot];
        if (candidate !== undefined && best !== undefined && candidate < best) pivot = i;
      }
      const canonical = [...cycle.slice(pivot), ...cycle.slice(0, pivot)];
      found.set(canonical.join('->'), canonical);
      return;
    }
    state.set(id, 'open');
    for (const next of edges.get(id) ?? []) visit(next, [...path, id]);
    state.set(id, 'done');
  };

  for (const id of edges.keys()) visit(id, []);
  return [...found.values()];
}

interface ContainerChildren {
  readonly parent: string;
  readonly term: ContainmentTerm;
  readonly children: readonly string[];
}

function containers(index: GraphIndex): readonly ContainerChildren[] {
  const result: ContainerChildren[] = [];
  for (const node of index.nodes) {
    for (const term of CONTAINMENT_TERMS) {
      const children = idsField(node, term);
      if (children.length > 0) result.push({ parent: node['@id'], term, children });
    }
  }
  return result;
}

function publishedMethods(index: GraphIndex): readonly MethodNode[] {
  return nodesOfType(index, NODE_TYPES.Method).filter(
    (node) => stringField(node, 'status') === 'PUBLISHED',
  );
}

function deliverableNodes(index: GraphIndex): readonly MethodNode[] {
  return nodesOfType(index, ...TYPE_ALIASES.deliverable);
}

function typeLabel(types: readonly NodeTypeIri[]): string {
  return types.length > 4 ? 'any method element' : types.join(' or ');
}

export const RULE_IMPLEMENTATIONS: readonly RuleImplementation[] = [
  {
    id: 'MTH-001',
    title: 'Dangling reference',
    description:
      'Every reference must resolve to a node in the same graph. A published method with an unresolvable edge renders as a broken link in the explorer.',
    severity: 'error',
    run({ index }) {
      const findings: RuleFinding[] = [];
      for (const reference of index.references) {
        if (SPECIALISED_REFERENCE_TERMS.includes(reference.property as ReferenceTerm)) continue;
        if (index.byId.has(reference.to)) continue;
        findings.push({
          nodeId: reference.from,
          property: reference.property,
          message: `references "${reference.to}", which is not in the graph`,
          fix: `Add a node with @id "${reference.to}" or remove it from ${reference.property}.`,
        });
      }
      return findings;
    },
  },
  {
    id: 'MTH-002',
    title: 'Duplicate @id',
    description:
      'An @id identifies exactly one node. Duplicates make every reference to that id ambiguous and break import idempotency.',
    severity: 'error',
    run({ index }) {
      return index.duplicateIds.map((id) => ({
        nodeId: id,
        property: '@id',
        message: `@id "${id}" is declared more than once`,
        fix: 'Give each node a unique @id, or merge the duplicate declarations into one node.',
      }));
    },
  },
  {
    id: 'MTH-003',
    title: 'Self reference',
    description:
      'Flow and containment edges may not point at their own node: an element cannot precede, depend on, contain or be a variant of itself.',
    severity: 'error',
    run({ index }) {
      const findings: RuleFinding[] = [];
      for (const reference of index.references) {
        if (!NON_REFLEXIVE_TERMS.includes(reference.property as ReferenceTerm)) continue;
        if (reference.from !== reference.to) continue;
        findings.push({
          nodeId: reference.from,
          property: reference.property,
          message: `${reference.property} points at its own node`,
          fix: `Remove "${reference.to}" from ${reference.property}.`,
        });
      }
      return findings;
    },
  },
  {
    id: 'MTH-004',
    title: 'Reference type mismatch',
    description:
      'A reference must point at a node of the type the property expects — performedBy at a Role, produces at a Deliverable, and so on.',
    severity: 'error',
    run({ index }) {
      const findings: RuleFinding[] = [];
      for (const reference of index.references) {
        const expected = REFERENCE_TARGETS[reference.property as ReferenceTerm] as
          | readonly NodeTypeIri[]
          | undefined;
        if (expected === undefined) continue;
        const target = index.byId.get(reference.to);
        if (target === undefined) continue;
        if ((expected as readonly string[]).includes(target['@type'])) continue;
        findings.push({
          nodeId: reference.from,
          property: reference.property,
          message: `${reference.property} points at "${reference.to}" of type ${target['@type']}; expected ${typeLabel(expected)}`,
          fix: `Point ${reference.property} at a ${typeLabel(expected)} node.`,
        });
      }
      return findings;
    },
  },
  {
    id: 'MTH-005',
    title: 'Variant target missing',
    description:
      'variantOf must name a method that exists. Reported as an error only when the set of known methods was supplied, because a variant legitimately references a method held in another document.',
    severity: 'error',
    run({ index, options }) {
      const known = options.knownMethodIds;
      if (known === undefined) return [];
      const findings: RuleFinding[] = [];
      for (const method of nodesOfType(index, NODE_TYPES.Method)) {
        const target = stringField(method, 'variantOf');
        if (target === undefined) continue;
        if (index.byId.has(target) || known.includes(target)) continue;
        findings.push({
          nodeId: method['@id'],
          property: 'variantOf',
          message: `variantOf names "${target}", which is not a known method`,
          fix: `Import the source method "${target}" first, or clear variantOf.`,
        });
      }
      return findings;
    },
  },
  {
    id: 'MTH-006',
    title: 'Variant target unresolved',
    description:
      'variantOf names a method that is not in this document. Legal for a standalone file, but compare and lineage stay unavailable until the source method is imported.',
    severity: 'warning',
    run({ index, options }) {
      if (options.knownMethodIds !== undefined) return [];
      const findings: RuleFinding[] = [];
      for (const method of nodesOfType(index, NODE_TYPES.Method)) {
        const target = stringField(method, 'variantOf');
        if (target === undefined || index.byId.has(target)) continue;
        findings.push({
          nodeId: method['@id'],
          property: 'variantOf',
          message: `variantOf names "${target}", which is not in this document`,
          fix: `Import "${target}" so the variant comparison can be built.`,
        });
      }
      return findings;
    },
  },
  {
    id: 'MTH-007',
    title: 'Deliverable template version missing',
    description:
      'A deliverable that points at a template version must point at one that exists, otherwise the published method has no body for that work product.',
    severity: 'error',
    run({ index }) {
      const findings: RuleFinding[] = [];
      for (const reference of index.references) {
        if (
          reference.property !== 'hasTemplateVersion' &&
          reference.property !== 'currentTemplateVersion' &&
          reference.property !== 'templateOverride'
        ) {
          continue;
        }
        if (index.byId.has(reference.to)) continue;
        findings.push({
          nodeId: reference.from,
          property: reference.property,
          message: `template version "${reference.to}" does not exist`,
          fix: `Add the dpem:TemplateVersion node "${reference.to}" or drop the reference.`,
        });
      }
      return findings;
    },
  },
  {
    id: 'MTH-008',
    title: 'Template version not linked to its deliverable',
    description:
      'A template version must be reachable from the deliverable it belongs to. A version that only points inwards is invisible to the authoring UI.',
    severity: 'error',
    run({ index }) {
      const findings: RuleFinding[] = [];
      for (const version of nodesOfType(index, NODE_TYPES.TemplateVersion)) {
        const id = version['@id'];
        const owner = stringField(version, 'templateOf');
        const inbound = (index.incoming.get(id) ?? []).filter(
          (reference) =>
            reference.property === 'hasTemplateVersion' ||
            reference.property === 'currentTemplateVersion',
        );
        if (owner === undefined) {
          if (inbound.length === 0) {
            findings.push({
              nodeId: id,
              property: 'templateOf',
              message: 'template version belongs to no deliverable',
              fix: 'Set templateOf, and list the version in the deliverable hasTemplateVersion.',
            });
          }
          continue;
        }
        const deliverable = index.byId.get(owner);
        if (deliverable === undefined) continue; // MTH-001 owns the dangling case.
        if (idsField(deliverable, 'hasTemplateVersion').includes(id)) continue;
        findings.push({
          nodeId: id,
          property: 'templateOf',
          message: `deliverable "${owner}" does not list this version in hasTemplateVersion`,
          fix: `Add "${id}" to hasTemplateVersion on "${owner}".`,
        });
      }
      return findings;
    },
  },
  {
    id: 'MTH-009',
    title: 'Duplicate template version number',
    description:
      'Template version numbers are monotonic per deliverable. Two versions with the same number make "the version a published method used" undecidable.',
    severity: 'error',
    run({ index }) {
      const findings: RuleFinding[] = [];
      for (const deliverable of deliverableNodes(index)) {
        const seen = new Map<number, string>();
        for (const versionId of idsField(deliverable, 'hasTemplateVersion')) {
          const version = index.byId.get(versionId);
          if (version === undefined) continue;
          const number = numberField(version, 'version');
          if (number === undefined) continue;
          const previous = seen.get(number);
          if (previous === undefined) {
            seen.set(number, versionId);
            continue;
          }
          findings.push({
            nodeId: versionId,
            property: 'version',
            message: `version ${String(number)} of "${deliverable['@id']}" is also declared by "${previous}"`,
            fix: 'Renumber one of the template versions.',
          });
        }
      }
      return findings;
    },
  },
  {
    id: 'MTH-010',
    title: 'Published method has no description',
    description:
      'A published method needs a one-line description; it is the text every catalogue and compare surface shows.',
    severity: 'warning',
    run({ index }) {
      return publishedMethods(index)
        .filter((method) => isBlank(stringField(method, 'description')))
        .map((method) => ({
          nodeId: method['@id'],
          property: 'description',
          message: 'published method has no description',
          fix: 'Add a one-line description before publishing.',
        }));
    },
  },
  {
    id: 'MTH-011',
    title: 'Published activity has no writeup',
    description:
      'Activities in a published method should carry the long-form writeup practitioners rely on. Missing writeups leave the explorer detail pane empty.',
    severity: 'warning',
    run({ index }) {
      if (publishedMethods(index).length === 0) return [];
      return nodesOfType(index, NODE_TYPES.Activity)
        .filter((activity) => isBlank(stringField(activity, 'writeup')))
        .map((activity) => ({
          nodeId: activity['@id'],
          property: 'writeup',
          message: 'activity has no writeup',
          fix: 'Author the activity writeup, or use AI assist to draft one and review it.',
        }));
    },
  },
  {
    id: 'MTH-012',
    title: 'Published deliverable has no template version',
    description:
      'A deliverable in a published method must have at least one template version, otherwise the method promises a work product with no content.',
    severity: 'error',
    run({ index }) {
      if (publishedMethods(index).length === 0) return [];
      return deliverableNodes(index)
        .filter(
          (deliverable) =>
            idsField(deliverable, 'hasTemplateVersion').length === 0 &&
            stringField(deliverable, 'currentTemplateVersion') === undefined,
        )
        .map((deliverable) => ({
          nodeId: deliverable['@id'],
          property: 'hasTemplateVersion',
          message: 'deliverable has no template version',
          fix: 'Create a template version with the markdown body before publishing.',
        }));
    },
  },
  {
    id: 'MTH-013',
    title: 'Tailoring decision has no rationale',
    description:
      'ADAPT and SKIP change the method away from its source; the workshop record is only defensible if each such decision states why.',
    severity: 'warning',
    run({ index }) {
      return nodesOfType(index, NODE_TYPES.TailoringDecision)
        .filter((decision) => {
          const verdict = stringField(decision, 'decision');
          return (
            (verdict === 'ADAPT' || verdict === 'SKIP') &&
            isBlank(stringField(decision, 'rationale'))
          );
        })
        .map((decision) => ({
          nodeId: decision['@id'],
          property: 'rationale',
          message: `${String(stringField(decision, 'decision'))} decision has no rationale`,
          fix: 'Record why the element is adapted or skipped.',
        }));
    },
  },
  {
    id: 'MTH-014',
    title: 'Published method has no writeup',
    description:
      'Publishing freezes a method forever. The method-level writeup — what this method is for and how to run it — is required before that happens.',
    severity: 'error',
    run({ index }) {
      return publishedMethods(index)
        .filter((method) => isBlank(stringField(method, 'writeup')))
        .map((method) => ({
          nodeId: method['@id'],
          property: 'writeup',
          message: 'published method has no writeup',
          fix: 'Author the method writeup, or move the method back to DRAFT.',
        }));
    },
  },
  {
    id: 'MTH-020',
    title: 'Duplicate order within a parent',
    description:
      'Siblings are rendered in order. Two siblings sharing an order value make the rendered sequence depend on storage order, which is not stable.',
    severity: 'error',
    run({ index }) {
      const findings: RuleFinding[] = [];
      for (const container of containers(index)) {
        const seen = new Map<number, string>();
        for (const childId of container.children) {
          const child = index.byId.get(childId);
          if (child === undefined) continue;
          const order = numberField(child, 'order');
          if (order === undefined) continue;
          const previous = seen.get(order);
          if (previous === undefined) {
            seen.set(order, childId);
            continue;
          }
          findings.push({
            nodeId: childId,
            property: 'order',
            message: `order ${String(order)} under "${container.parent}" is also used by "${previous}"`,
            fix: `Renumber the siblings of "${container.parent}" so each order is unique.`,
          });
        }
      }
      return findings;
    },
  },
  {
    id: 'MTH-021',
    title: 'Order values are not contiguous',
    description:
      'Sibling order values should be a contiguous run. Gaps usually mean an element was deleted without renumbering. Meta stages are exempt because they are deliberately parked out of band.',
    severity: 'warning',
    run({ index }) {
      const findings: RuleFinding[] = [];
      for (const container of containers(index)) {
        const orders: number[] = [];
        for (const childId of container.children) {
          const child = index.byId.get(childId);
          if (child === undefined) continue;
          if (booleanField(child, 'isMetaStage') === true) continue;
          const order = numberField(child, 'order');
          if (order !== undefined) orders.push(order);
        }
        const unique = [...new Set(orders)].sort((a, b) => a - b);
        if (unique.length < 2) continue;
        const first = unique[0];
        const last = unique[unique.length - 1];
        if (first === undefined || last === undefined) continue;
        if (last - first === unique.length - 1) continue;
        findings.push({
          nodeId: container.parent,
          property: container.term,
          message: `${container.term} order values are not contiguous: ${unique.join(', ')}`,
          fix: 'Renumber the children so order runs without gaps.',
        });
      }
      return findings;
    },
  },
  {
    id: 'MTH-022',
    title: 'Orphan activity',
    description:
      'Every activity must hang off a parent — a stage or a work practice. An orphan activity is unreachable from the method and never renders.',
    severity: 'error',
    run({ index }) {
      return nodesOfType(index, NODE_TYPES.Activity)
        .filter((activity) => {
          const inbound = index.incoming.get(activity['@id']) ?? [];
          return !inbound.some((reference) => reference.property === 'hasActivity');
        })
        .map((activity) => ({
          nodeId: activity['@id'],
          property: 'hasActivity',
          message: 'activity has no parent stage or work practice',
          fix: `Add "${activity['@id']}" to hasActivity on the stage or work practice that owns it.`,
        }));
    },
  },
  {
    id: 'MTH-023',
    title: 'Orphan stage',
    description:
      'A stage that no method and no phase lists is dead weight in the graph — it exports but never appears in the pipeline.',
    severity: 'warning',
    run({ index }) {
      return nodesOfType(index, NODE_TYPES.Stage)
        .filter((stage) => {
          const inbound = index.incoming.get(stage['@id']) ?? [];
          return !inbound.some((reference) => reference.property === 'hasStage');
        })
        .map((stage) => ({
          nodeId: stage['@id'],
          property: 'hasStage',
          message: 'stage is not listed by any method or phase',
          fix: `Add "${stage['@id']}" to hasStage on the method that owns it.`,
        }));
    },
  },
  {
    id: 'MTH-024',
    title: 'Element has more than one parent',
    description:
      'Activities and work practices have exactly one owner. Two owners means the element renders twice and its order is ambiguous. Only hasActivity and hasWorkPractice are checked — a stage legitimately belongs to both a method and a phase.',
    severity: 'error',
    run({ index }) {
      const findings: RuleFinding[] = [];
      for (const [target, inbound] of index.incoming) {
        for (const term of ['hasActivity', 'hasWorkPractice'] as const) {
          const parents = [
            ...new Set(inbound.filter((r) => r.property === term).map((r) => r.from)),
          ];
          if (parents.length < 2) continue;
          findings.push({
            nodeId: target,
            property: term,
            message: `claimed by ${String(parents.length)} parents via ${term}: ${parents.join(', ')}`,
            fix: 'Keep one owner and reference the element from the others by a flow edge instead.',
          });
        }
      }
      return findings;
    },
  },
  {
    id: 'MTH-025',
    title: 'Work practice hierarchy cycle',
    description:
      'Work practices nest to form a taxonomy, so the containment edges must form a tree. A cycle makes the taxonomy infinite.',
    severity: 'error',
    run({ index }) {
      const edges = new Map<string, string[]>();
      const link = (parent: string, child: string): void => {
        const bucket = edges.get(parent);
        if (bucket === undefined) edges.set(parent, [child]);
        else bucket.push(child);
      };
      for (const practice of nodesOfType(index, NODE_TYPES.WorkPractice)) {
        for (const child of idsField(practice, 'hasWorkPractice')) link(practice['@id'], child);
        const parent = stringField(practice, 'parentPractice');
        if (parent !== undefined) link(parent, practice['@id']);
      }
      return findCycles(edges).map((cycle) => ({
        nodeId: cycle[0],
        property: 'parentPractice',
        message: `work practice hierarchy cycle: ${[...cycle, cycle[0] ?? ''].join(' -> ')}`,
        fix: 'Break the cycle by removing one parentPractice or hasWorkPractice edge.',
      }));
    },
  },
  {
    id: 'MTH-030',
    title: 'Cycle in precedes',
    description:
      'precedes is the forward flow of the method and must be acyclic. Deliberate backward edges belong on loopsTo, which this rule ignores by construction.',
    severity: 'error',
    run({ index }) {
      return findCycles(edgeMap(index, 'precedes')).map((cycle) => ({
        nodeId: cycle[0],
        property: 'precedes',
        message: `precedes cycle: ${[...cycle, cycle[0] ?? ''].join(' -> ')}`,
        fix: 'Remove one precedes edge, or express the backward step as loopsTo.',
      }));
    },
  },
  {
    id: 'MTH-031',
    title: 'Gate not reachable',
    description:
      'A gate must govern something. A gate nothing points at is a control point that can never be evaluated, and it never appears on the pipeline.',
    severity: 'warning',
    run({ index }) {
      return nodesOfType(index, ...TYPE_ALIASES.gate)
        .filter((gate) => {
          const inbound = index.incoming.get(gate['@id']) ?? [];
          return !inbound.some(
            (reference) =>
              reference.property === 'governedByGate' || reference.property === 'hasGate',
          );
        })
        .map((gate) => ({
          nodeId: gate['@id'],
          property: 'governedByGate',
          message: 'gate is not referenced by any stage, work practice or activity',
          fix: `Set governedByGate to "${gate['@id']}" on the element it controls.`,
        }));
    },
  },
  {
    id: 'MTH-032',
    title: 'Cycle in dependsOn',
    description:
      'dependsOn expresses a hard prerequisite. A cycle of prerequisites can never be satisfied and blocks any scheduling of the method.',
    severity: 'error',
    run({ index }) {
      return findCycles(edgeMap(index, 'dependsOn')).map((cycle) => ({
        nodeId: cycle[0],
        property: 'dependsOn',
        message: `dependsOn cycle: ${[...cycle, cycle[0] ?? ''].join(' -> ')}`,
        fix: 'Remove one dependsOn edge to break the prerequisite loop.',
      }));
    },
  },
  {
    id: 'MTH-034',
    title: 'Client contribution not declared',
    description:
      'A node a client brought into an adoption carries provenance "client". The method node must list every one of them in clientContributions, so a reader can see what in the method is the client\'s without scanning it.',
    severity: 'warning',
    run({ index }) {
      const clientNodes = index.nodes.filter(
        (node) => stringField(node, 'provenance') === 'client',
      );
      if (clientNodes.length === 0) return [];

      const method = nodesOfType(index, NODE_TYPES.Method)[0];
      // No method node at all is MTH-002's finding, not this rule's: reporting an
      // undeclared contribution as well would name the same defect twice.
      if (method === undefined) return [];

      const declared = new Set(idsField(method, 'clientContributions'));
      return clientNodes
        .filter((node) => !declared.has(node['@id']))
        .map((node) => ({
          nodeId: method['@id'],
          property: 'clientContributions',
          message: `client node ${node['@id']} is not declared in clientContributions`,
          fix: `Add "${node['@id']}" to the method's clientContributions.`,
        }));
    },
  },
];

/**
 * Rule metadata for the authoring validation table. Logic deliberately excluded
 * so the UI can render the catalogue without pulling the validator in.
 */
export const RULES: readonly Rule[] = RULE_IMPLEMENTATIONS.map(
  ({ id, title, description, severity }) => ({ id, title, description, severity }),
);

export const RULES_BY_ID: ReadonlyMap<string, Rule> = new Map(RULES.map((rule) => [rule.id, rule]));

export { indexGraph };
